import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  updatePassword,
} from "aws-amplify/auth";


if (typeof window !== "undefined" && window.sessionStorage) {
  const originalClear = sessionStorage.clear.bind(sessionStorage);
  sessionStorage.clear = () => {
    const isLoggedInSingle = sessionStorage.getItem("isLoggedInSingle");
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");

    originalClear();

    if (isLoggedInSingle) sessionStorage.setItem("isLoggedInSingle", isLoggedInSingle);
    if (isLoggedIn) sessionStorage.setItem("isLoggedIn", isLoggedIn);
  };
}

const USER_ATTR_TTL_MS = 2 * 60 * 1000;
let cachedAttributes = null;
let attributesCacheExpiry = 0;

let pendingSession = null;
let pendingAuthUser = null;

const clearAuthCache = () => {
  cachedAttributes = null;
  attributesCacheExpiry = 0;
  pendingSession = null;
  pendingAuthUser = null;
  try {
    localStorage.removeItem("amplify_cached_user_attributes");
    localStorage.removeItem("amplify_user_attributes_expiry");
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("isLoggedInSingle");
    sessionStorage.removeItem("user_id");
  } catch (e) { }
};


const toLegacyToken = (token) => {
  if (!token) return null;

  return {
    ...token,
    jwtToken: token.toString(),
    payload: token.payload || {},
    getJwtToken: () => token.toString(),
  };
};

const toLegacySession = (session) => {
  const idToken = toLegacyToken(session.tokens?.idToken);
  const accessToken = toLegacyToken(session.tokens?.accessToken);

  return {
    ...session,
    idToken,
    accessToken,
    refreshToken: session.tokens?.refreshToken,
    clockDrift: session.tokens?.clockDrift,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    getRefreshToken: () => session.tokens?.refreshToken,
  };
};

const toLegacyChallengeName = (signInStep) => {
  const challengeNames = {
    CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
    CONFIRM_SIGN_IN_WITH_SMS_CODE: "SMS_MFA",
    CONFIRM_SIGN_IN_WITH_TOTP_CODE: "SOFTWARE_TOKEN_MFA",
    CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: "CUSTOM_CHALLENGE",
  };

  return challengeNames[signInStep] || signInStep;
};

const currentSession = async () => {
  // If there's already an in-flight request, reuse it
  if (pendingSession) return pendingSession;

  pendingSession = fetchAuthSession()
    .then((session) => {
      if (!session.tokens?.idToken || !session.tokens?.accessToken) {
        throw new Error("No current authenticated user");
      }
      return toLegacySession(session);
    })
    .finally(() => {
      pendingSession = null;
    });

  return pendingSession;
};


const fetchUserAttributesCached = async () => {
  const now = Date.now();
  if (cachedAttributes && now < attributesCacheExpiry) {
    return cachedAttributes;
  }
  try {
    const storedAttributes = localStorage.getItem("amplify_cached_user_attributes");
    const storedExpiry = localStorage.getItem("amplify_user_attributes_expiry");
    if (storedAttributes && storedExpiry && now < Number(storedExpiry)) {
      cachedAttributes = JSON.parse(storedAttributes);
      attributesCacheExpiry = Number(storedExpiry);
      return cachedAttributes;
    }
  } catch (e) {
    console.error("Error reading auth cache from localStorage", e);
  }
  cachedAttributes = await fetchUserAttributes();
  attributesCacheExpiry = now + USER_ATTR_TTL_MS;
  try {
    localStorage.setItem("amplify_cached_user_attributes", JSON.stringify(cachedAttributes));
    localStorage.setItem("amplify_user_attributes_expiry", attributesCacheExpiry.toString());
  } catch (e) {
    console.error("Error saving auth cache to localStorage", e);
  }

  return cachedAttributes;
};

const currentAuthenticatedUser = async () => {
  // If there's already an in-flight request, reuse it
  if (pendingAuthUser) return pendingAuthUser;

  pendingAuthUser = (async () => {
    const [user, attributes, signInUserSession] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributesCached(),
      currentSession(),
    ]);

    return {
      ...user,
      id: user.userId,
      attributes,
      signInUserSession,
      username: user.username,
    };
  })().finally(() => {
    pendingAuthUser = null;
  });

  return pendingAuthUser;
};

const currentUserInfo = async () => {
  const [user, attributes] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributesCached(),
  ]);

  return {
    id: user.userId,
    username: user.username,
    attributes,
  };
};

const signIn = async (username, password) => {
  const result = await amplifySignIn({ username, password });

  if (result.isSignedIn) {
    return currentAuthenticatedUser();
  }

  return {
    ...result,
    challengeName: toLegacyChallengeName(result.nextStep?.signInStep),
  };
};

const completeNewPassword = async (_user, newPassword, userAttributes) => {
  const input = { challengeResponse: newPassword };

  if (userAttributes) {
    input.options = { userAttributes };
  }

  const result = await confirmSignIn(input);

  if (result.isSignedIn) {
    return currentAuthenticatedUser();
  }

  return {
    ...result,
    challengeName: toLegacyChallengeName(result.nextStep?.signInStep),
  };
};

const changePassword = async (_user, oldPassword, newPassword) => {
  await updatePassword({ oldPassword, newPassword });
  return "SUCCESS";
};

const signUp = ({ username, password, attributes, options, ...rest }) =>
  amplifySignUp({
    username,
    password,
    options: {
      ...options,
      userAttributes: attributes,
    },
    ...rest,
  });

const forgotPassword = (username) => resetPassword({ username });

const forgotPasswordSubmit = (username, confirmationCode, newPassword) =>
  confirmResetPassword({ username, confirmationCode, newPassword });

const resendSignUp = (username) => resendSignUpCode({ username });

export const Auth = {
  changePassword,
  completeNewPassword,
  confirmSignUp: (username, confirmationCode) =>
    confirmSignUp({ username, confirmationCode }),
  currentAuthenticatedUser,
  currentSession,
  currentUserInfo,
  forgotPassword,
  forgotPasswordSubmit,
  resendSignUp,
  signIn,
  signOut: async (...args) => {
    clearAuthCache();
    return amplifySignOut(...args);
  },
  signUp,
};
